// SPDX-License-Identifier: AGPL-3.0-or-later
import './styles.css';
import { initTooltip, hideTip } from './components/tooltip';
import { initGlossary, gloss, hideGlossary } from './glossary';
import { esc, num, pctv, compact, money } from './format';
import type { Dataset, ClassId, Route } from './types';
import { renderCountryDrawer, renderStateDrawer } from './drilldown';
import { renderOverview } from './views/overview';
import { renderAtlas } from './views/atlas';
import { renderCountries } from './views/countries';
import { renderMap } from './views/map';
import { renderTrends } from './views/trends';
import { renderAssets } from './views/assets';
import { renderExplorer } from './views/explorer';
import { renderInsights } from './views/insights';

const VIEWS = [
  { id: 'overview', label: 'Overview', render: renderOverview },
  { id: 'atlas', label: 'The Atlas', render: renderAtlas },
  { id: 'countries', label: 'Countries', render: renderCountries },
  { id: 'map', label: 'Map', render: renderMap },
  { id: 'trends', label: 'Trends', render: renderTrends },
  { id: 'assets', label: 'By asset', render: renderAssets },
  { id: 'explorer', label: 'Explorer', render: renderExplorer },
  { id: 'insights', label: 'Insights', render: renderInsights },
] as const;

export type ViewId = (typeof VIEWS)[number]['id'];
const STORE_KEY = 'au-foreign-owned:view';

function parseHash(): Route {
  const params = new URLSearchParams(location.hash.replace(/^#/, ''));
  const view = (params.get('v') ?? '') as ViewId;
  const known = VIEWS.some((v) => v.id === view);
  const stored = localStorage.getItem(STORE_KEY) as ViewId | null;
  return {
    view: known ? view : (VIEWS.some((v) => v.id === stored) && stored ? stored : 'overview'),
    country: params.get('c') ?? undefined,
    state: params.get('s') ?? undefined,
    cls: (params.get('cls') as ClassId) ?? undefined,
    filter: params.get('q') ?? undefined,
  };
}

export function navigate(patch: Partial<Route>, replace = false): void {
  const cur = parseHash();
  const next = { ...cur, ...patch };
  const params = new URLSearchParams();
  params.set('v', next.view);
  if (next.country) params.set('c', next.country);
  if (next.state) params.set('s', next.state);
  if (next.cls) params.set('cls', next.cls);
  if (next.filter) params.set('q', next.filter);
  const url = `#${params.toString()}`;
  if (replace) { history.replaceState(null, '', url); render(); }
  else location.hash = url;
}

let data: Dataset | null = null;

async function load(): Promise<Dataset> {
  const bust = `?v=${Date.now().toString(36).slice(0, 6)}`;
  const names = ['meta', 'classes', 'matrix'];
  const [meta, classes, matrix] = await Promise.all(
    names.map(async (f) => {
      const res = await fetch(`data/${f}.json${bust}`);
      if (!res.ok) throw new Error(`Could not load ${f}.json (HTTP ${res.status})`);
      return res.json();
    }),
  );
  return { meta, classes, matrix } as Dataset;
}

function shell(): void {
  const app = document.getElementById('app')!;
  app.innerHTML = `
    <header class="site-header">
      <div class="header-inner">
        <div class="brand">
          <img class="logo" src="/favicon.svg" alt="" width="22" height="22" />
          <h1>Foreign Ownership</h1>
          <span class="brand-sub">Who owns Australia — by country and asset</span>
        </div>
        <div class="header-spacer"></div>
        <button class="icon-btn" id="about-btn" aria-label="About this site" title="About this site">?</button>
      </div>
      <div class="nav-wrap">
        <nav class="nav-tabs" role="tablist" aria-label="Views">
          ${VIEWS.map((v) => `<button class="nav-tab" role="tab" data-view="${v.id}" aria-selected="false">${v.label}</button>`).join('')}
        </nav>
      </div>
    </header>
    <main class="main-content" id="view-root"><div class="skeleton"></div></main>
    <footer class="site-footer">
      <div class="footer-inner">
        <p id="footer-source"></p>
        <p>Built by <a href="https://benrichardson.dev/">benrichardson.dev</a> · <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more tools &amp; sites</a></p>
      </div>
    </footer>
    <div class="overlay" id="overlay"></div>
    <aside class="drawer" id="drawer" role="dialog" aria-label="Detail" aria-modal="true"></aside>
    <div class="modal" id="about-modal" role="dialog" aria-label="About this site" aria-modal="true"></div>
  `;

  app.querySelectorAll<HTMLButtonElement>('.nav-tab').forEach((btn) => {
    btn.addEventListener('click', () => {
      localStorage.setItem(STORE_KEY, btn.dataset.view!);
      navigate({ view: btn.dataset.view as ViewId, country: undefined, state: undefined, cls: undefined, filter: undefined });
    });
  });

  const header = document.querySelector<HTMLElement>('.site-header')!;
  const syncHeaderHeight = () => document.documentElement.style.setProperty('--sticky-top', `${header.offsetHeight}px`);
  syncHeaderHeight();
  new ResizeObserver(syncHeaderHeight).observe(header);

  const drawerEl = document.getElementById('drawer')!;
  drawerEl.addEventListener('transitionend', (e) => {
    if (e.propertyName === 'transform' && !drawerEl.classList.contains('open')) drawerEl.style.display = 'none';
  });

  document.getElementById('about-btn')!.addEventListener('click', openAbout);
  document.getElementById('overlay')!.addEventListener('click', closeOverlays);
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeOverlays(); });
}

function closeOverlays(): void {
  document.getElementById('overlay')!.classList.remove('open');
  document.getElementById('drawer')!.classList.remove('open');
  document.getElementById('about-modal')!.classList.remove('open');
  hideGlossary();
  const cur = parseHash();
  if (cur.country || cur.state) navigate({ country: undefined, state: undefined }, true);
}

function openAbout(): void {
  const m = document.getElementById('about-modal')!;
  const meta = data?.meta;
  const c = meta?.counts;
  m.innerHTML = `
    <button class="icon-btn modal-close" aria-label="Close">✕</button>
    <h2>About this site</h2>
    <p>Who owns Australia? This unifies all six asset classes of the Australian Taxation Office's ${gloss('register', 'Register of Foreign Ownership of Australian Assets')} — agricultural land, water entitlements, residential and commercial land, business interests, and mining tenements — into one place you can search, map, rank and read, by country and by state.</p>

    <h3>The one thing to take away</h3>
    <p>There is <strong>no single "biggest foreign owner of Australia"</strong>: the answer flips completely depending on what you count. The ${gloss('sourceCountry', 'source country')} that leads farmland (measured in hectares) is not the one that leads water, homes, commercial dollars, mining tenements or business interests.</p>

    <h3>Where the data comes from</h3>
    <p>The Register is a live database of self-registrations by ${gloss('foreignPerson', 'foreign persons')}; the ATO reports on it once a year, as at 30&nbsp;June. This site reads the six published workbooks and joins them to official ABS state boundaries. Nothing is modelled or estimated.</p>
    <ul>
      <li>ATO Register of Foreign Ownership of Australian Assets — report at 30&nbsp;June ${esc(String(c?.latestYear ?? ''))} (six asset-class workbooks)</li>
      <li>ABS ASGS 2021 state &amp; territory boundaries</li>
    </ul>

    <h3>The headline numbers</h3>
    <ul>
      <li><strong>${pctv(c?.agForeignSharePct)}</strong> of Australian agricultural land carries a foreign interest; <strong>${pctv(c?.waterForeignSharePct)}</strong> of water entitlements — both broadly flat for a decade.</li>
      <li>The largest single farmland holder is the <strong>${esc(c?.agTop?.name ?? '')}</strong> (${compact(c?.agTop?.value)} ha); China has fallen to ${compact(c?.agChinaNow)} ha, down about ${pctv(Math.abs((c?.agChinaDropPct ?? 0) * 100), 0)} from its peak.</li>
      <li>Foreign-registered commercial property is worth <strong>${money(c?.commercialValueTotal)}</strong>. ${num(c?.residentialAcqLatest)} homes were acquired by foreign buyers in the latest year.</li>
      <li><strong>${num(c?.nCountries)}</strong> source countries appear across the six classes.</li>
    </ul>

    <h3>How to read the numbers — important caveats</h3>
    <ul>
      <li><strong>Source country is country of <em>control</em>, not incorporation.</strong> ${gloss('sourceCountry', 'The ATO attributes each interest')} to where the controlling person or fund manager sits — which is why fund domiciles like the Cayman Islands appear prominently.</li>
      <li><strong>A foreign interest is not full foreign ownership.</strong> The Register records parcels where ${gloss('foreignPerson', 'foreign persons')} hold a substantial stake; the same parcel can still be largely Australian-owned.</li>
      <li><strong>The classes use different units and can't be added together</strong> — hectares, gigalitres, dollars, and counts of homes, businesses or tenements.</li>
      <li><strong>Small cells are ${gloss('suppression', 'suppressed')}</strong> ("≤5") to protect privacy — shown as "not published", never as zero.</li>
      <li><strong>Residential figures are a ${gloss('residentialFlow', 'yearly flow of acquisitions')}</strong>, not a running total like the other classes.</li>
    </ul>

    <h3>Updates</h3>
    <p>The ATO reports once a year; this site refreshes on the same annual cadence. Report: ${esc(meta ? new Date(meta.reportDate).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' }) : '')}.</p>
  `;
  m.querySelector('.modal-close')!.addEventListener('click', closeOverlays);
  document.getElementById('overlay')!.classList.add('open');
  m.classList.add('open');
  (m.querySelector('.modal-close') as HTMLElement).focus();
}

export function openCountry(name: string): void { navigate({ country: name }); }
export function openState(code: string): void { navigate({ state: code }); }

function render(): void {
  if (!data) return;
  const route = parseHash();
  document.querySelectorAll<HTMLButtonElement>('.nav-tab').forEach((b) => {
    b.setAttribute('aria-selected', String(b.dataset.view === route.view));
  });

  const root = document.getElementById('view-root')!;
  const view = VIEWS.find((v) => v.id === route.view) ?? VIEWS[0];
  root.scrollTop = 0;
  if (!route.country && !route.state) window.scrollTo(0, 0);
  hideTip();
  try {
    (view.render as (r: HTMLElement, d: Dataset, route: Route) => void)(root, data, route);
  } catch (err) {
    root.innerHTML = `<div class="error-state"><p>Something went wrong drawing this view.</p><p>${esc(err instanceof Error ? err.message : String(err))}</p></div>`;
  }

  // Any navigation dismisses the transient About modal.
  document.getElementById('about-modal')!.classList.remove('open');

  const drawer = document.getElementById('drawer')!;
  const overlay = document.getElementById('overlay')!;
  let opened = false;
  if (route.country) {
    const cn = data.matrix.countries.find((x) => x.name === route.country);
    if (cn) { renderCountryDrawer(drawer, cn, data); opened = true; }
  } else if (route.state) {
    renderStateDrawer(drawer, route.state, data);
    opened = true;
  }
  if (opened) {
    drawer.querySelector('.drawer-close')?.addEventListener('click', closeOverlays);
    overlay.classList.add('open');
    drawer.style.display = 'block';
    void drawer.offsetWidth;
    drawer.classList.add('open');
    (drawer.querySelector('.drawer-close') as HTMLElement | null)?.focus();
  } else {
    drawer.classList.remove('open');
    overlay.classList.remove('open');
  }
}

async function boot(): Promise<void> {
  shell();
  initTooltip();
  initGlossary();
  window.addEventListener('hashchange', render);

  try {
    data = await load();
  } catch (err) {
    document.getElementById('view-root')!.innerHTML = `
      <div class="error-state">
        <p>Could not load the foreign-ownership data.</p>
        <p>${esc(err instanceof Error ? err.message : String(err))}</p>
        <p><button class="icon-btn" style="width:auto;padding:0 12px;border-radius:8px" onclick="location.reload()">Retry</button></p>
      </div>`;
    return;
  }

  const c = data.meta.counts;
  document.getElementById('footer-source')!.innerHTML =
    `Source: Australian Taxation Office, Register of Foreign Ownership of Australian Assets — report at 30 June ${esc(String(c.latestYear))} (CC BY 4.0), and ABS ASGS 2021 boundaries. ` +
    `Source country = country of control, not incorporation. Asset classes use different units and can't be summed.`;

  render();
}

void boot();
